/**
 * Open-from-pin service for content-bearing "snapshot" workspace pins
 * (`tableView` / text `query` / cells `notebook`). This is the ONLY entry
 * point the Object Explorer's pinned tree calls to open a pin as a linked
 * workspace tab.
 *
 * Spec rules enforced here:
 *  - FOCUS-not-duplicate: if a tab already linked to this pin (same
 *    connection) is open, activate the most-recently-active one instead of
 *    opening a second tab.
 *  - Snapshot payloads are deserialized through the sanitizer first; any
 *    dropped filters/cells surface as one "N … could not be restored" toast.
 *  - tableView pins take their database from the pin ENVELOPE (F10) — the
 *    payload's own `database` may be stale — and open with
 *    `pendingColumnReconcile` so Task 14 reconciles columns against the live
 *    schema.
 *  - Notebook cell ids are RE-MINTED on every open (G14) so opening the same
 *    pin twice never collides cell ids across tabs.
 *  - The opened tab gets `sourcePin` set atomically as part of the open — no
 *    window where the tab exists unlinked.
 *  - Queries/notebooks NEVER auto-execute: `deserializeTab` builds idle state
 *    and this service never touches the execution pipeline.
 *
 * File pins (`payload.source === 'file'`) are opened by the pin-dedicated file
 * path in `openFilePin` (explicit envelope binding, `sourcePin`-only dedup,
 * on-focus disk revalidation) — see that module for the G2/G3 rationale.
 */
import { toast } from '../../../../components/ui/toaster';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { useWorkspaceStore } from '../../../workspace/core/store/workspaceStore';
import { deserializeTab } from '../../../sessions/services/tabDeserializer';
import type { SerializedTab } from '../../../sessions/types/sessionTypes';
import type { Tab, TabConnectionInfo } from '../../../workspace/core/types/workspaceTypes';
import type { PinnedWorkspaceRef } from '../../../../types/electron';
import {
  sanitizeTableViewPayload,
  sanitizeNotebookCells,
  type PinnedNotebookCells,
} from './pinPayloadSanitizer';
import { openFilePin } from './openFilePin';
import {
  isFileSourcePin,
  pinWorkspaceType,
} from '../../../workspace/common/services/workspacePinCreate';
import { safeConnectionAlias, safeConnectionServer } from '../../../../types/safeConnectionDisplay';

export type WorkspaceState = ReturnType<typeof useWorkspaceStore.getState>;

/**
 * Tabs currently linked to `(connectionId, pinId)` in the given workspace
 * snapshot. Callers that await between reading and acting MUST re-derive this
 * from a FRESH `useWorkspaceStore.getState()` after each await (K3) — the tab
 * set can change while a disk read is in flight.
 */
export function findLinkedTabs(connectionId: string, pinId: string, ws: WorkspaceState): Tab[] {
  return ws.tabs.filter(
    (t): boolean =>
      t.sourcePin != null &&
      t.sourcePin.pinId === pinId &&
      t.sourcePin.connectionId === connectionId,
  );
}

/**
 * In-flight opens keyed by the (connectionId, pinId) PAIR (K3). A file pin's
 * open awaits a disk read; without single-flight two rapid opens of the same
 * pin both see "no linked tab" and duplicate. The second concurrent call awaits
 * and returns the first's outcome. Entries self-clear on completion.
 *
 * The key is `JSON.stringify([connectionId, pinId])`, NOT a `:`-joined concat —
 * plain concatenation collides for pairs like ("a:b","c") vs ("a","b:c"),
 * making one pin silently never open (L4).
 */
const inFlightOpens = new Map<string, Promise<void>>();

export function openWorkspacePin(args: {
  connectionId: string;
  ref: PinnedWorkspaceRef;
}): Promise<void> {
  const key = JSON.stringify([args.connectionId, args.ref.id]);
  const existing = inFlightOpens.get(key);
  if (existing) return existing;
  const p = runOpenWorkspacePin(args).finally(() => {
    inFlightOpens.delete(key);
  });
  inFlightOpens.set(key, p);
  return p;
}

async function runOpenWorkspacePin({
  connectionId,
  ref,
}: {
  connectionId: string;
  ref: PinnedWorkspaceRef;
}): Promise<void> {
  // File pins own their linked-tab handling (focus + disk revalidation, G2) AND
  // re-read workspace state after their disk await (K3), so they route first —
  // before the snapshot focus-dedup below reads a snapshot of tabs.
  if (isFileSourcePin(ref)) {
    const mode = await openFilePin({ connectionId, ref });
    if (mode) trackPinOpen(ref, mode);
    return;
  }

  // Snapshot pins have no await before this point, so a single fresh read is
  // safe (single-flight above still prevents concurrent duplication).
  const ws = useWorkspaceStore.getState();
  const linked = findLinkedTabs(connectionId, ref.id, ws);
  if (linked.length > 0) {
    ws.setActiveTab(mostRecentlyActive(linked, ws).id);
    trackPinOpen(ref, 'focus');
    return;
  }

  const serialized = buildSerializedTab(connectionId, ref);
  const tab = deserializeTab(serialized);
  // Runtime-only flags applied AFTER deserialize — neither is part of the
  // serialized shape.
  //  - table-explorer: `pendingColumnReconcile` (Task 14 consumes it).
  //  - compare tabs: `autoCompare: false` (W5.1c). Opening a pin must NEVER
  //    launch a comparison. This is belt-and-braces on purpose: the pin payload
  //    allowlist already excludes `autoCompare` and `deserializeTab` never
  //    reads it, so the flag would be `undefined` here anyway — but "undefined
  //    happens to be falsy" is an invariant nothing states and nothing tests,
  //    and the cost of it silently flipping is a user staring at a 45-minute
  //    scan they never asked for (the whole reason W3.4 closed the equivalent
  //    navigation hole). Written explicitly so a test can assert it and a
  //    future `autoCompare ?? true` default can't quietly reopen the hole.
  // Each compare type gets its OWN branch: a combined
  // `schema-compare || data-compare` test widens `tab.content` back to the
  // union of both content types, which TypeScript then can't assign to either
  // member. Separate branches keep the discriminated narrowing.
  const finalTab: Tab =
    tab.type === 'table-explorer'
      ? { ...tab, content: { ...tab.content, pendingColumnReconcile: true } }
      : tab.type === 'schema-compare'
        ? { ...tab, content: { ...tab.content, autoCompare: false } }
        : tab.type === 'data-compare'
          ? { ...tab, content: { ...tab.content, autoCompare: false } }
          : tab;
  ws.addRestoredTab(finalTab);
  ws.setSourcePin(finalTab.id, { connectionId, pinId: ref.id });
  trackPinOpen(ref, 'new');
}

/**
 * Picks which already-open linked tab to focus. The store keeps no MRU log,
 * so the currently-active linked tab is the best "most recently active"
 * proxy; otherwise fall back to the last linked tab in tabs order.
 */
export function mostRecentlyActive(linked: Tab[], ws: WorkspaceState): Tab {
  const active = linked.find((t) => t.id === ws.activeTabId);
  return active ?? linked[linked.length - 1];
}

function buildSerializedTab(connectionId: string, ref: PinnedWorkspaceRef): SerializedTab {
  const base = {
    id: crypto.randomUUID(),
    title: ref.name,
    connectionInfo: connectionInfoFor(connectionId, ref.database),
  };

  if (ref.kind === 'tableView') {
    const { payload, droppedCount } = sanitizeTableViewPayload(ref.payload);
    if (droppedCount > 0) {
      toast.warning({
        title: 'Pinned view restored',
        description: `${droppedCount} filter${droppedCount === 1 ? '' : 's'} could not be restored`,
      });
    }
    return {
      ...base,
      type: 'table-explorer',
      // Envelope database is authoritative (F10) — the payload's own
      // `database` may be stale after a rename/move.
      content: { ...payload, database: ref.database },
    };
  }

  // Comparison pins (B5/F07). The payload IS the serialized tab content, so it
  // goes straight through `deserializeTab`, which rebuilds the configuration
  // and marks the tab `needsRefresh` — results are never persisted, so the tab
  // opens idle with the "Run Compare to refresh" banner. A target on a
  // disconnected (or deleted) connection is fine: the tab opens, and the
  // workspace connection banner reads BOTH endpoints out of `content`
  // (`useActiveTabConnectionNeed`) to offer a reconnect.
  if (ref.kind === 'dataCompare') {
    return { ...base, type: 'data-compare', content: ref.payload };
  }
  if (ref.kind === 'schemaCompare') {
    return { ...base, type: 'schema-compare', content: ref.payload };
  }

  if (ref.kind === 'query') {
    // Only the text variant reaches here; the file variant branches earlier.
    const query = ref.payload.source === 'text' ? ref.payload.query : '';
    return { ...base, type: 'query', content: { query } };
  }

  // notebook — only the cells variant reaches here (file branches earlier).
  const cellsIn = ref.payload.source === 'cells' ? ref.payload.cells : [];
  const metadata = ref.payload.source === 'cells' ? ref.payload.metadata : {};
  const { cells, droppedCount, adjustedCount } = sanitizeNotebookCells(cellsIn);
  const restoreMessage = buildNotebookRestoreMessage(droppedCount, adjustedCount);
  if (restoreMessage) {
    toast.warning({ title: 'Pinned notebook restored', description: restoreMessage });
  }
  // G14: re-mint every cell id so opening the same pin twice never collides
  // cell ids across the two notebook tabs.
  const reminted = cells.map((c: PinnedNotebookCells[number]) => ({
    ...c,
    id: crypto.randomUUID(),
  }));
  return {
    ...base,
    type: 'notebook',
    content: { cells: reminted, metadata, executionCounter: 0, isDirty: false },
  };
}

/**
 * One honest sentence for the notebook-restore toast (K7). A dropped cell was
 * REMOVED; an adjusted item survived but was changed (coerced cellType /
 * stripped chart). Reports each with correct pluralization, combined when both
 * occur. Returns `null` when nothing changed (no toast).
 */
function buildNotebookRestoreMessage(dropped: number, adjusted: number): string | null {
  const parts: string[] = [];
  if (dropped > 0) {
    parts.push(`${dropped} cell${dropped === 1 ? '' : 's'} could not be restored`);
  }
  if (adjusted > 0) {
    parts.push(
      `${adjusted} notebook item${adjusted === 1 ? '' : 's'} ${adjusted === 1 ? 'was' : 'were'} adjusted`,
    );
  }
  return parts.length > 0 ? parts.join('; ') : null;
}

/**
 * Builds the tab's `connectionInfo` from the connection store — mirrors
 * `openTableExplorerTab.ts`. Resilient to a not-currently-active connection
 * (pins may be opened for a saved-but-disconnected connection): falls back to
 * the saved-connection config and always carries `connectionId` + `database`.
 */
export function connectionInfoFor(connectionId: string, database: string): TabConnectionInfo {
  const { connections, activeConnections } = useConnectionStore.getState();
  const savedConnection = connections.find((c) => c.id === connectionId);
  const activeConnection = activeConnections.find((c) => c.id === connectionId);
  const connectionConfig = savedConnection ?? activeConnection?.connection;
  return {
    connectionId,
    connectionName: safeConnectionAlias(connectionConfig, 'Unnamed Connection'),
    serverHost: safeConnectionServer(activeConnection?.connection ?? savedConnection),
    database,
  };
}

/**
 * Fire-and-forget analytics for a pin-open (non-React context). Guards
 * `typeof window` so it is a no-op under unit tests / the main process, per
 * the repo's non-React analytics convention (see `resultsExportApi.ts`).
 */
function trackPinOpen(ref: PinnedWorkspaceRef, mode: 'focus' | 'new'): void {
  if (typeof window === 'undefined') return;
  if (!window.electronAPI?.analytics?.track) return;
  const source = isFileSourcePin(ref) ? 'file' : 'snapshot';
  const workspaceType = pinWorkspaceType(ref.kind);
  window.electronAPI.analytics
    .track('feature:workspace_pin_open', { workspaceType, source, mode })
    .catch(() => {
      // Silently ignore analytics errors.
    });
}
